import {
    type ReactElement,
    isValidElement,
    Children
} from "react";
import type IComposerActionsProps from "./components/actions/type";
import type IComposerAttachmentsProps from "./components/attachments/type";
import type IComposerInputProps from "./components/input/type";
import type IComposerSubmitProps from "./components/submit/type";
import type {
    ComposerFormatSizeConstantType,
    ComposerPendingConstantType,
    ComposerSlotsConstantType,
    ComposerEnterConstantType,
    ComposerFileConstantType,
    ComposerHintConstantType,
    ComposerSizeConstantType,
    ComposerKeyConstantType,
    ComposerPendingState,
    ComposerAttachment,
    ComposerSlots,
    ComposerHint,
    ComposerSize
} from "./type";
import ComposerActions from "./components/actions";
import ComposerAttachments from "./components/attachments";
import ComposerInput from "./components/input";
import ComposerSubmit from "./components/submit";
import {
    getInputGroupTextAreaIsSubmit
} from "../inputGroup/components/textArea/stylesheet";
import {
    formatNumber
} from "../../helpers/locale/format";

export const COMPOSER_FILE_SIZE_UNITS: Array<string> = [
    "B",
    "KB",
    "MB",
    "GB"
];

export const COMPOSER_FILE_SIZE_STEP = 1024;

export const getComposerSlots = ({
    children
}: ComposerSlotsConstantType): ComposerSlots => {
    const slots: ComposerSlots = {
        actions: []
    };

    Children.toArray(children).forEach((child) => {
        if(!isValidElement(child)) {
            return;
        }

        if(child.type === ComposerActions) {
            slots.actions.push(...Children.toArray((child as ReactElement<IComposerActionsProps>).props.children));

            return;
        }

        if(child.type === ComposerAttachments) {
            slots.attachments = child as ReactElement<IComposerAttachmentsProps>;

            return;
        }

        if(child.type === ComposerInput) {
            slots.input = child as ReactElement<IComposerInputProps>;

            return;
        }

        if(child.type === ComposerSubmit) {
            slots.submit = child as ReactElement<IComposerSubmitProps>;
        }
    });

    return slots;
};

export const getComposerSize = ({
    pointerType,
    size
}: ComposerSizeConstantType): ComposerSize => {
    if(size) {
        return size;
    }

    return pointerType === "coarse" ? "large" : "medium";
};

export const getComposerPendingState = ({
    attachments
}: ComposerPendingConstantType): ComposerPendingState => {
    return {
        isPending: attachments.some((item) => item.uploadState === "uploading" || item.uploadState === "processing"),
        isFailed: attachments.some((item) => item.uploadState === "error")
    };
};

export const getComposerHint = ({
    disabledText,
    isDisabled,
    isOffline,
    isPending,
    isFailed,
    localize
}: ComposerHintConstantType): ComposerHint | undefined => {
    if(isDisabled) {
        return disabledText ? {
            text: disabledText,
            icon: "info"
        } : undefined;
    }

    if(isFailed) {
        return {
            text: localize("attachment-error-hint"),
            icon: "info"
        };
    }

    if(isPending) {
        return {
            text: localize("upload-pending-hint"),
            icon: "info"
        };
    }

    if(isOffline) {
        return {
            text: localize("offline-queue-hint"),
            icon: "offline"
        };
    }

    return undefined;
};

export const getComposerIsEnterSubmit = ({
    submitBehaviour,
    pointerType
}: ComposerEnterConstantType): boolean => {
    if(submitBehaviour === "auto") {
        return pointerType === "fine";
    }

    return submitBehaviour === "enter";
};

export const getComposerIsSubmitKey = ({
    isEnterSubmit,
    event
}: ComposerKeyConstantType): boolean => {
    if(!isEnterSubmit) {
        return false;
    }

    return getInputGroupTextAreaIsSubmit({
        submitBehaviour: "enter",
        event
    });
};

export const getComposerFormattedSize = ({
    locale,
    bytes
}: ComposerFormatSizeConstantType): string => {
    let value = bytes;

    let unitIndex = 0;

    while(value >= COMPOSER_FILE_SIZE_STEP && unitIndex < COMPOSER_FILE_SIZE_UNITS.length - 1) {
        value /= COMPOSER_FILE_SIZE_STEP;

        unitIndex += 1;
    }

    const formatted = formatNumber({
        options: {
            maximumFractionDigits: unitIndex === 0 ? 0 : 1
        },
        locale,
        value
    });

    return `${formatted} ${COMPOSER_FILE_SIZE_UNITS[unitIndex]}`;
};

export const getComposerAttachmentFromFile = ({
    locale,
    file,
    id
}: ComposerFileConstantType): ComposerAttachment => {
    const isImage = file.type.startsWith("image/");

    return {
        description: getComposerFormattedSize({
            bytes: file.size,
            locale
        }),
        source: isImage ? {
            uri: URL.createObjectURL(file)
        } : undefined,
        uploadState: "done",
        title: file.name,
        file,
        id
    };
};
